import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { toast } from 'react-toastify';
import Menu from '../../../Menu/Menu';
import MenuItem from '../../../Menu/MenuItem';
import { CustomInput } from '../../../../components/Input/Input';
import { CustomButton } from '../../../Button/Button';
import SharingModalButton from '../SharingModalButton/SharingModalButton';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import Images from '../../../../constants/Images';
import styles from './sharing.module.scss';
import VideoService from '../../../../service/VideoService';
import Toast from '../../../../../common/utils/Toast';
import usePrevious from '../../../../../common/hooks/usePrevious';
import { useTranslation } from 'react-i18next';
import WarningNotification from '../../../WarningNotification/WarningNotification';
import { ToastNotification } from '../../../Toast/Toast';
import { useUserContext } from '../../../../context/UserContext';
import useAccessibleClick from '../../../../../common/hooks/useAccessibleClick';

const SharingFooter = ({ videoId, isCanChangeVideoPassword, videoUrl, isSkipAuthToken }) => {
  let userDetails = JSON.parse(localStorage.getItem('userDetails')) || null;

  const passwordMenuRef = useRef(null);
  const [clickedCopyLink, setClickedCopyLink] = useState(false);
  const [showPasswordMenu, setShowPasswordMenu] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isProtected, setIsProtected] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [passwordsMatch, setPasswordMatch] = useState(false);
  const { logFirebaseEvent } = useUserContext();
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [warningChangeModal, setWarningChangeModal] = useState(false);
  const [warningDeleteModal, setWarningDeleteModal] = useState(false);

  const previousIsProtected = usePrevious(isProtected);
  const { t } = useTranslation();

  const handleShowPasswordMenu = () => {
    setShowPasswordMenu(true);
  };

  const passwordActionRef = useAccessibleClick(handleShowPasswordMenu);

  useEffect(() => {
    isProtectedVideo(videoId);
  }, [isProtected]);

  const isProtectedVideo = async storyId => {
    try {
      const video = await VideoService.getVideoDetails(storyId, isSkipAuthToken);
      if (video.isProtected) {
        setIsProtected(true);
      }
    } catch (error) {
      console.log('err', error);
    }
  };

  useEffect(() => {
    const resetClickedCopyLink = () => {
      setTimeout(() => {
        setClickedCopyLink(false);
      }, 3000);
    };
    clickedCopyLink && resetClickedCopyLink();
  }, [clickedCopyLink]);

  useOutsideClick(passwordMenuRef, () => {
    if (showPasswordMenu === true) {
      setShowPasswordMenu(false);
    }
  });

  useEffect(() => {
    setPasswordMatch(password && confirmPassword && password === confirmPassword);

    if (password && confirmPassword && password !== confirmPassword) {
      setError(t('video.settings.share.password_are_not_matching'));
    } else {
      setError('');
    }
  }, [password, confirmPassword]);

  const handleCopyLink = async () => {
    setClickedCopyLink(true);
    logFirebaseEvent('copy_link_cl', { story_id: videoId });
    navigator.clipboard.writeText(videoUrl);
  };

  const onChangeInput = e => {
    const field = e.target.name;

    if (field === 'password') {
      setPassword(e.target.value?.trim());
    }

    if (field === 'confirmPassword') {
      setConfirmPassword(e.target.value?.trim());
    }
  };

  const savePassword = async () => {
    setWarningChangeModal(false);
    try {
      await VideoService.updateVideo(videoId, { password });
      setIsProtected(true);
      setShowForm(false);
      toast.dismiss();
      ToastNotification({
        message: previousIsProtected
          ? t('video.settings.share.password_has_been_updated')
          : t('video.settings.share.new_password_has_been_updated'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (error) {
      setShowForm(false);
      setPassword('');
      setConfirmPassword('');
      Toast.error(t('video.settings.share.error_setting_the_password'));
    }
  };

  const onCheckPassword = async () => {
    if (isProtected) {
      setWarningChangeModal(true);
    } else {
      savePassword();
    }
  };

  const onRemovePassword = async () => {
    setShowPasswordMenu(false);
    setWarningDeleteModal(false);
    try {
      const saveResult = await VideoService.updateVideo(videoId, { password: '' });
      setIsProtected(false);
      toast.dismiss();
      ToastNotification({
        message: t('video.settings.share.password_has_been_removed'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (error) {
      Toast.error(t('video.settings.share.error_removing_the_password'));
    }
  };

  return (
    <div className={cx(styles.sharingFooter)}>
      <h4 tabIndex={0}>{t('video.settings.share.privacy_settings')}</h4>
      <div className={styles.videoWithPasswordContainer}>
        <p tabIndex={0}>{t('video.settings.share.privacy_settings.anyone_with_the_link')}</p>
        <div className={styles.passwordAction}>
          <span tabIndex={0}>{t('video.settings.share.privacy_settings.can_view')}</span>
        </div>
      </div>
      {isCanChangeVideoPassword && (
        <div className={styles.videoWithPasswordContainer}>
          {isProtected ? (
            <p tabIndex={0}>
              {t('video.settings.share.privacy_settings.protected_with_password')}{' '}
              {!isMobileView && <img src={Images.greenCheck} alt="" />}
            </p>
          ) : (
            <p tabIndex={0}>{t('video.settings.share.privacy_settings.protect_with_password')}</p>
          )}
          <div className={styles.passwordAction}>
            {showPasswordMenu && showForm == false ? (
              <Menu
                ref={passwordMenuRef}
                listMode
                simple
                customStyles={{
                  bottom: '110px', // 8em
                  right: '24px',
                  filter: 'none',
                  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
                }}
              >
                {isProtected ? (
                  <>
                    <MenuItem
                      simple
                      onClick={() => {
                        setShowForm(true);
                        setShowPasswordMenu(false);
                      }}
                    >
                      {t('video.settings.share.privacy_settings.change_password')}
                    </MenuItem>
                    <MenuItem
                      simple
                      onClick={() => {
                        setWarningDeleteModal(true);
                      }}
                    >
                      {t('video.settings.share.privacy_settings.remove')}
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem
                      simple
                      onClick={() => {
                        setShowForm(true);
                        setShowPasswordMenu(false);
                      }}
                    >
                      {t('video.settings.share.privacy_settings.enabled')}
                    </MenuItem>
                  </>
                )}
              </Menu>
            ) : null}
            <span ref={passwordActionRef} role="button" tabIndex={0} onClick={() => handleShowPasswordMenu()}>
              {isProtected ? t('video.settings.share.privacy_settings.manage') : t('video.settings.share.privacy_settings.disabled')}
              {
                <img
                  src={Images.ArrowDownIcon}
                  alt={t('accessibility.arrow_down')}
                  className={cx(styles.arrowButton, { [styles.turnArrow]: showPasswordMenu && showForm === false })}
                />
              }
            </span>
          </div>
        </div>
      )}

      {showForm ? (
        <div className={styles.inlineForm}>
          <div className={styles.inputsInline}>
            <div className={styles.inputInline}>
              <CustomInput
                isShow
                value={password}
                type="password"
                name="password"
                customStyles={{ paddingBottom: '16px' }}
                placeholder={t('general.password.enter_password')}
                invalid={error !== ''}
                onChange={event => onChangeInput(event)}
              />
            </div>
            <div className={styles.inputInline}>
              <CustomInput
                isShow
                value={confirmPassword}
                type="password"
                name="confirmPassword"
                customStyles={{ paddingBottom: '16px' }}
                placeholder={t('general.password.confirm_password')}
                invalid={error !== ''}
                validateMessage={error}
                onChange={event => onChangeInput(event)}
              />
            </div>
          </div>
          <div className={styles.actionButtons}>
            <CustomButton
              variant="basic"
              label={t('general.password.save_password.button')}
              onClick={onCheckPassword}
              disabled={!passwordsMatch}
            />
            <CustomButton
              label={t('general.cancel')}
              onClick={() => {
                setError('');
                setShowForm(false);
              }}
              className={styles.cancelButton}
            />
          </div>
        </div>
      ) : null}

      {!showForm && (
        <div className={styles.copy}>
          <SharingModalButton onClick={handleCopyLink} clicked={clickedCopyLink}>
            {clickedCopyLink ? t('general.video_action_button.link_copied') : t('general.video_action_button.copy_link')}
          </SharingModalButton>
        </div>
      )}
      <WarningNotification
        open={warningChangeModal}
        onClose={() => setWarningChangeModal(false)}
        onSubmit={savePassword}
        title={t('shared_video_modal.warning.change_password')}
        text={
          <>
            {t('shared_video_modal.warning.change_password.text.part_one')}{' '}
            <strong>{t('shared_video_modal.warning.change_password.text.part_two')}</strong>
          </>
        }
        btnText={t('shared_video_modal.warning.change_password')}
      />
      <WarningNotification
        open={warningDeleteModal}
        onClose={() => setWarningDeleteModal(false)}
        onSubmit={onRemovePassword}
        title={t('shared_video_modal.warning.delete_password')}
        text={
          <>
            {t('shared_video_modal.warning.delete_password.text.part_one')}{' '}
            <strong>{t('shared_video_modal.warning.delete_password.text.part_two')}</strong>
          </>
        }
        btnText={t('shared_video_modal.warning.delete_password')}
      />
    </div>
  );
};

export default SharingFooter;
